<template lang="pug">
.cmp(ref="rootRef" :class="{ 'is-active': revealed }" role="img" :aria-label="ariaLabel")
  .cmp-legend(aria-hidden="true")
    span.cmp-legend-item
      i.cmp-swatch.is-panda
      | {{ pandaCoreName }}
    span.cmp-legend-item
      i.cmp-swatch.is-sing
      | {{ singBoxName }}
    span.cmp-legend-item(v-if="hasEngine2")
      i.cmp-swatch.is-engine2
      | {{ engine2Name }}
  .cmp-row(v-for="(row, index) in rows" :key="row.id" :style="{ '--delay': `${index * 110}ms` }")
    .cmp-label {{ row.label }}
    .cmp-bars
      .cmp-bar.is-panda(:style="{ '--w': row.pandaWidth }")
        .cmp-fill
        span.cmp-value {{ row.pandaText }}
      .cmp-bar.is-sing(:style="{ '--w': row.singWidth }")
        .cmp-fill
        span.cmp-value {{ row.singText }}
      .cmp-bar.is-engine2(v-if="row.engine2Text" :style="{ '--w': row.engine2Width }")
        .cmp-fill
        span.cmp-value {{ row.engine2Text }}
    .cmp-deltas
      .cmp-comparison(v-for="(comparison, index) in row.comparisons" :key="comparison.rival" :title="comparison.caption")
        span(:class="[index === 0 ? 'cmp-delta' : 'cmp-delta2', { 'is-lead': comparison.leads }]") {{ comparison.text }}
        small.cmp-comparison-name {{ comparison.caption }}
</template>

<script setup lang="ts">
import type { PairMetric } from '~/utils/pandaCoreBenchmarks'
import { formatMultiplier, formatSignedPercent, rankComparisons } from '~/utils/pandaCoreBenchmarks'

const props = withDefaults(defineProps<{
  metrics: readonly PairMetric[]
  labels: Record<string, string>
  decimals?: number
  /** 'multiplier' shows "4.3×"; 'percent' shows "−65%". */
  deltaStyle?: 'multiplier' | 'percent'
  pandaCoreName?: string
  singBoxName?: string
  engine2Name?: string
}>(), {
  decimals: 1,
  deltaStyle: 'multiplier',
  pandaCoreName: 'PandaCore',
  singBoxName: 'Open-source engine 1',
  engine2Name: 'Open-source engine 2',
})

const { t } = useI18n()

const hasEngine2 = computed(() => props.metrics.some(metric => metric.engine2 !== undefined))

const rootRef = ref<HTMLElement>()
const { revealed } = useRevealOnce(rootRef)

const formatter = computed(() => new Intl.NumberFormat('en-US', {
  minimumFractionDigits: props.decimals,
  maximumFractionDigits: props.decimals,
}))

const rows = computed(() => {
  const max = Math.max(...props.metrics.flatMap(metric => [metric.pandaCore, metric.singBox, metric.engine2 ?? 0]))
  if (!(max > 0)) {
    throw new Error('Comparison bars need at least one positive measurement')
  }
  return props.metrics.map((metric) => {
    const label = props.labels[metric.id]
    if (!label) {
      throw new Error(`Missing label for benchmark metric ${metric.id}`)
    }
    return {
      id: metric.id,
      label,
      pandaWidth: metric.pandaCore / max,
      singWidth: metric.singBox / max,
      pandaText: formatter.value.format(metric.pandaCore),
      singText: formatter.value.format(metric.singBox),
      engine2Text: metric.engine2 !== undefined ? formatter.value.format(metric.engine2) : null,
      engine2Width: metric.engine2 !== undefined ? metric.engine2 / max : 0,
      comparisons: rankComparisons(metric).map(comparison => ({
        ...comparison,
        caption: t('pandacore.throughput.vs_caption', { name: comparison.rival === 'singBox' ? props.singBoxName : props.engine2Name }),
        text: comparison.tie ? t('pandacore.throughput.verdict_tie') : props.deltaStyle === 'percent' ? formatSignedPercent(comparison.deltaPercent) : formatMultiplier(comparison.multiplier),
      })),
    }
  })
})

const ariaLabel = computed(() => rows.value
  .map(row => `${row.label}: ${props.pandaCoreName} ${row.pandaText}, ${props.singBoxName} ${row.singText}${row.engine2Text ? `, ${props.engine2Name} ${row.engine2Text}` : ''}; ${row.comparisons.map(comparison => `${comparison.text} ${comparison.caption}`).join(', ')}`)
  .join('; '))
</script>

<style scoped>
.cmp {
  display: grid;
  gap: 0.95rem;
  container-type: inline-size;
}

.cmp-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.1rem;
  margin-bottom: 0.15rem;
  color: var(--pc-faint);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.cmp-legend-item {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 0.45rem;
}

.cmp-swatch {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 2px;
}

.cmp-swatch.is-panda {
  background: var(--pc-green);
}

.cmp-swatch.is-sing {
  background: var(--pc-sing);
}

.cmp-swatch.is-engine2 {
  background: var(--pc-engine2);
}

.cmp-row {
  display: grid;
  grid-template-columns: minmax(5.5rem, 8.5rem) minmax(0, 1fr) minmax(5rem, 7rem);
  align-items: center;
  gap: 0.9rem;
}

.cmp-label {
  color: var(--pc-copy);
  font-size: 0.86rem;
  font-weight: 600;
  line-height: 1.25;
}

.cmp-bars {
  display: grid;
  gap: 0.34rem;
  padding-right: 4.6rem;
  min-width: 0;
}

.cmp-bar {
  position: relative;
  height: 0.62rem;
}

.cmp-fill {
  position: absolute;
  inset: 0 auto 0 0;
  width: calc(var(--w) * 100%);
  min-width: 3px;
  border-radius: 2px;
  clip-path: inset(0 100% 0 0 round 2px);
  transition: clip-path 1.1s cubic-bezier(0.16, 1, 0.3, 1) var(--delay);
}

.cmp-bar.is-panda .cmp-fill {
  background: var(--pc-green);
}

.cmp-bar.is-engine2 .cmp-fill {
  background: var(--pc-engine2);
  opacity: 0.75;
}

.cmp-bar.is-sing .cmp-fill {
  background: var(--pc-sing);
  opacity: 0.72;
}

.cmp.is-active .cmp-fill {
  clip-path: inset(0 0 0 0 round 2px);
}

.cmp-value {
  position: absolute;
  top: 50%;
  left: calc(var(--w) * 100% + 0.55rem);
  transform: translateY(-50%) translateX(-0.4rem);
  color: var(--pc-text);
  font-size: 0.74rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  white-space: nowrap;
  opacity: 0;
  transition:
    opacity 0.5s ease calc(var(--delay) + 0.55s),
    transform 0.6s cubic-bezier(0.16, 1, 0.3, 1) calc(var(--delay) + 0.55s);
}

.cmp-bar.is-engine2 .cmp-value {
  color: var(--pc-faint);
}

.cmp-bar.is-sing .cmp-value {
  color: var(--pc-copy);
}

.cmp.is-active .cmp-value {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
}

.cmp-deltas {
  display: grid;
  justify-items: end;
  gap: 0.15rem;
}

.cmp-comparison {
  display: grid;
  justify-items: end;
  gap: 0.15rem;
}

.cmp-comparison-name {
  color: var(--pc-faint);
  font-size: 0.62rem;
  line-height: 1.2;
  text-align: right;
}

.cmp-delta2 {
  color: var(--pc-faint);
  font-size: 0.78rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.cmp-delta {
  justify-self: end;
  color: var(--pc-copy);
  font-family: Metropolis, 'SF Pro Display', 'Segoe UI', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  line-height: 1;
  opacity: 0;
  transform: translateY(0.35rem);
  transition:
    opacity 0.5s ease calc(var(--delay) + 0.75s),
    transform 0.6s cubic-bezier(0.16, 1, 0.3, 1) calc(var(--delay) + 0.75s);
}

.cmp-delta.is-lead {
  color: var(--pc-green-ink);
}

.cmp.is-active .cmp-delta {
  opacity: 1;
  transform: translateY(0);
}

@container (max-width: 640px) {
  .cmp-row {
    grid-template-columns: minmax(0, 1fr) 5rem;
    row-gap: 0.4rem;
  }

  .cmp-label {
    grid-column: 1 / -1;
    font-size: 0.8rem;
  }

  .cmp-bars {
    padding-right: 4.2rem;
  }
}
</style>
